import { MessageSquare, Send } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { Spinner } from '@/components/ui';
import { Button } from '@/components/ui';
import { useAddComment, useComments } from '@/hooks/use-loans';
import { useAuthStore } from '@/stores/auth-store';
import { toast } from '@/stores/toast-store';

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString('en-AU', { month: 'short', day: 'numeric' });
}

interface Props {
  loanId: string;
}

export function CommentsTab({ loanId }: Props) {
  const user = useAuthStore(s => s.user);
  const { data: comments, isLoading } = useComments(loanId);
  const addComment = useAddComment();
  const [body, setBody] = useState('');

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!body.trim()) return;
    addComment.mutate(
      { loanId, body: body.trim() },
      {
        onSuccess: () => {
          setBody('');
          toast.success('Comment added');
        },
      },
    );
  }

  return (
    <div className="space-y-4">
      {/* Comment input */}
      <form onSubmit={handleSubmit} className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex gap-3">
          {user && (
            <div className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center shrink-0 mt-0.5">
              <span className="text-xs font-semibold">
                {user.firstName[0]}
                {user.lastName[0]}
              </span>
            </div>
          )}
          <div className="flex-1">
            <textarea
              value={body}
              onChange={e => setBody(e.target.value)}
              placeholder="Add a comment…"
              rows={3}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm
                         placeholder:text-gray-400 focus:border-blue-500 focus:ring-1
                         focus:ring-blue-500 outline-none transition-colors resize-none"
            />
            <div className="flex justify-end mt-2">
              <Button
                variant="primary"
                type="submit"
                disabled={!body.trim() || addComment.isPending}
                className="flex items-center gap-1.5 px-3 py-1.5 text-white rounded-md text-sm font-medium hover: disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                {addComment.isPending ? (
                  <Spinner size="lg" className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Send className="w-3.5 h-3.5" />
                )}
                Comment
              </Button>
            </div>
          </div>
        </div>
      </form>

      {/* Comments list */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12 text-gray-400">
          <Spinner size="lg" className="w-5 h-5 animate-spin mr-2" />
          Loading comments…
        </div>
      ) : !comments || comments.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-gray-400">
          <MessageSquare className="w-8 h-8 mb-2" />
          <p className="text-sm">No comments yet</p>
        </div>
      ) : (
        <div className="space-y-3">
          {comments.map(comment => (
            <div key={comment.id} className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="flex items-center gap-2.5 mb-2">
                <div className="w-7 h-7 rounded-full bg-gray-100 text-gray-600 flex items-center justify-center shrink-0">
                  <span className="text-[10px] font-semibold">
                    {comment.author
                      ? `${comment.author.firstName[0]}${comment.author.lastName[0]}`
                      : '?'}
                  </span>
                </div>
                <span className="text-sm font-medium text-gray-900">
                  {comment.author
                    ? `${comment.author.firstName} ${comment.author.lastName}`
                    : 'Unknown'}
                </span>
                <span className="text-xs text-gray-400">{timeAgo(comment.createdAt)}</span>
              </div>
              <p className="text-sm text-gray-700 whitespace-pre-wrap pl-9">{comment.body}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
